import { useCallback, useMemo } from 'react';

import Flag from 'react-world-flags';

import { AVAILABLE_MEXICO_STATES, AVAILABLE_US_STATES, COUNTRY_LIST } from 'data-layer';
import { useIntl } from 'localization';
import { TOption } from 'ui/src/molecules/Dropdown/Dropdown';

import { useGetCountries } from './core/store/useGetCountries';

const useGetCountryOptions = (skipLoading?: boolean) => {
  const intl = useIntl();

  const { data: countries, isLoading } = useGetCountries({ enabled: !skipLoading });

  const loc = useMemo(() => intl.locale.split('-')[0], [intl.locale]);
  let countryOptions: TOption[] = [];

  const orderCountries = useMemo(
    () =>
      COUNTRY_LIST.map((cc) => ({
        value: cc,
        label: intl.formatMessage({ id: `cc.${cc}` }),
        icon: (
          <div className="w-6 h-6 flex items-center justify-center">
            <Flag
              className="rounded-sm object-cover"
              style={{ width: '18px', height: '14px' }}
              code={cc}
            />
          </div>
        ),
      })).sort((a, b) => a.label.localeCompare(b.label, loc)),
    [],
  );

  const getAvailableStates = useCallback((countryCode: string) => {
    switch (countryCode) {
      case 'US':
        return AVAILABLE_US_STATES.map((state) => ({
          value: state.abbreviation,
          label: state.name,
        }));
      case 'MX':
        return AVAILABLE_MEXICO_STATES.map((state) => ({
          value: state.abbreviation,
          label: state.name,
        }));
      default:
        return [];
    }
  }, []);

  if (!isLoading) {
    countryOptions = Object.entries(countries || [])
      .map(([, value]) => ({
        icon: (
          <div className="w-6 h-6 flex items-center justify-center">
            <Flag
              className="rounded-sm object-cover"
              style={{ width: '18px', height: '14px' }}
              code={value.countryCode}
            />
          </div>
        ),
        label: intl.formatMessage({ id: `cc.${value.countryCode}` }),
        value: value.countryCode,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, loc));
  }

  return { countryOptions, isLoading, orderCountries, getAvailableStates };
};

export default useGetCountryOptions;
